import React, { useEffect, useState, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLocation } from "react-router-dom";
import cmsPageAPI from "../../api/cmsPageApi";
import SEO from "../../components/SEO";

gsap.registerPlugin(ScrollTrigger);

function AboutHero() {
  const eyebrowRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const descRef = useRef(null);
  const scrollRef = useRef(null);
  const scrollFill = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
        delay: 0.15,
      });
      tl.to(eyebrowRef.current, { opacity: 1, duration: 0.7 }, 0)
        .to(line1Ref.current, { opacity: 1, y: 0, duration: 1.0 }, 0.25)
        .to(line2Ref.current, { opacity: 1, y: 0, duration: 1.0 }, 0.4)
        .to(descRef.current, { opacity: 1, duration: 0.75 }, 0.7)
        .to(scrollRef.current, { opacity: 1, duration: 0.6 }, 1.0);

      gsap.to(scrollFill.current, {
        y: "100%",
        duration: 1.3,
        ease: "power2.inOut",
        repeat: -1,
        delay: 1.2,
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <section className="about-hero" aria-label="BEGUM'S brand story">
      <div className="about-hero-bg" aria-hidden="true" />
      <div className="about-hero-shimmer" aria-hidden="true" />
      <div className="about-hero-grain" aria-hidden="true" />
      {["tl", "tr", "bl", "br"].map((p) => (
        <div
          key={p}
          className={`about-hero-corner about-hero-corner--${p}`}
          aria-hidden="true"
        />
      ))}

      <div className="about-hero-content">
        <p className="about-hero-eyebrow" ref={eyebrowRef}>
          <span className="about-hero-eyebrow-line" aria-hidden="true" />
          Our Story
          <span className="about-hero-eyebrow-line" aria-hidden="true" />
        </p>
        <h1 className="about-hero-heading">
          <span className="about-hero-heading-line" ref={line1Ref}>
            The Essence
          </span>
          <span className="about-hero-heading-line" ref={line2Ref}>
            of <em>BEGUM'S</em>
          </span>
        </h1>
        <p className="about-hero-desc" ref={descRef}>
          Rooted in timeless Indian craftsmanship, BEGUM'S celebrates the beauty
          of heritage textiles and contemporary elegance — where every thread
          carries a story centuries in the making.
        </p>
      </div>

      <div className="about-hero-scroll" ref={scrollRef} aria-hidden="true">
        <span className="about-hero-scroll-text">Discover</span>
        <div className="about-hero-scroll-track">
          <div className="about-hero-scroll-fill" ref={scrollFill} />
        </div>
      </div>
    </section>
  );
}

function AboutHeritage({ description }) {
  const eyebrowRef = useRef(null);
  const headingRef = useRef(null);
  const bodyRef = useRef(null);
  const ruleRef = useRef(null);
  const statsRef = useRef(null);
  const tagRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: eyebrowRef.current,
          start: "top 80%",
          once: true,
        },
        defaults: { ease: "power3.out" },
      });
      tl.to(eyebrowRef.current, { opacity: 1, duration: 0.65 }, 0)
        .to(headingRef.current, { opacity: 1, y: 0, duration: 0.85 }, 0.18)
        .to(bodyRef.current, { opacity: 1, duration: 0.65 }, 0.42)
        .to(ruleRef.current, { opacity: 1, duration: 0.55 }, 0.55)
        .to(statsRef.current, { opacity: 1, duration: 0.65 }, 0.65)
        .to(tagRef.current, { opacity: 1, duration: 0.65 }, 0.5);
    });
    return () => ctx.revert();
  }, []);

  return (
    <section className="about-heritage" aria-labelledby="heritage-heading">
      <div className="about-heritage-grid">
        {/* Visual Panel */}
        <div className="about-heritage-visual">
          <div className="about-heritage-img-placeholder" aria-hidden="true" />
          <img
            className="about-heritage-img"
            src="https://i.pinimg.com/1200x/c9/da/81/c9da81e2e8ea0240db9de5e5b0dfe3e3.jpg"
            alt="BEGUM'S artisans at work"
            loading="lazy"
          />
          <div className="about-heritage-img-overlay" aria-hidden="true" />
          <div className="about-heritage-craft-tag" ref={tagRef}>
            <p className="about-heritage-craft-tag-inner">Est. 2012</p>
            <p className="about-heritage-craft-tag-name">
              Heritage
              <br />
              Craft
            </p>
          </div>
        </div>

        {/* Content Panel */}
        <div className="about-heritage-content">
          <div className="about-heritage-vline" aria-hidden="true" />
          <div className="about-heritage-inner">
            {/* <p className="about-heritage-eyebrow" ref={eyebrowRef}>
              <span className="about-heritage-eyebrow-line" aria-hidden="true" />
              Heritage & Craft
            </p>
            <h2 className="about-heritage-heading" id="heritage-heading" ref={headingRef}>
              Inspired by<br /><em>Heritage</em>
            </h2>
            <p className="about-heritage-body" ref={bodyRef}>
              BEGUM'S was born from a deep reverence for India's incomparable textile 
              traditions. From the brocade looms of Banaras to the resist-dyeing 
              traditions of Rajasthan, we travel the subcontinent to source fabrics 
              that carry centuries of knowledge in every weave.
              <br /><br />
              Our partnerships with artisan communities are built on trust, fair 
              exchange, and a shared belief that handcraft is not a relic — it is 
              the most sophisticated form of making.
            </p>
            <div className="about-heritage-rule" ref={ruleRef} aria-hidden="true" />
            <div className="about-heritage-stats" ref={statsRef} aria-label="Brand highlights">
              {[
                { num:"12+",  label:"Craft Traditions"  },
                null,
                { num:"1200+",label:"Partner Artisans"  },
                null,
                { num:"48",   label:"Craft Regions"     },
              ].map((item, i) =>
                item === null
                  ? <div key={i} className="about-heritage-stat-div" aria-hidden="true" />
                  : (
                    <div key={i} className="about-heritage-stat">
                      <span className="about-heritage-stat-num">{item.num}</span>
                      <span className="about-heritage-stat-label">{item.label}</span>
                    </div>
                  )
              )}
            </div> */}
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutUs() {
  const location = useLocation();
  const path = location.pathname.split("/")[1];

  const [metaData, setMetaData] = useState({});
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    fetchAboutDetails();
  }, [path]);

  const fetchAboutDetails = async () => {
    try {
      const res = await cmsPageAPI.details(path);

      if (res.code === 200) {
        const result = res.data;
        console.log(result);

        console.log(result.meta);

        setTitle(result.title);
        setDescription(result.content);
        setMetaData(result?.meta);
      }
    } catch (err) {}
  };

  return (
    <>
      <SEO meta={metaData} />
      <main aria-label="About BEGUM'S">
        <AboutHero />
        <AboutHeritage description={description} />
      </main>
    </>
  );
}

export default AboutUs;
